import React, {useEffect, useState} from 'react'
import TablePagination from '../../components/TablePagination/TablePagination'
import {flexRender, getCoreRowModel, getFilteredRowModel, useReactTable} from '@tanstack/react-table'
import * as Yup from 'yup'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import clsx from 'clsx'
import {ToastContainer} from 'react-toastify'
import TitleComponent from '../../components/TitleComponent/TitleComponent'
import moments from 'moment'
import {useNavigate, useParams} from 'react-router-dom'
import {PaginationModel} from '../../models/PaginationModel'
import {ParamsModelZahtjevZaNabavu, ZahtjevZaNabavu} from '../../models/ZahtevZaNabavu'
import {fetchZahtjevZaNabavu, getAllZahtjevZaNabavu, saveZahtjevZaNabavu} from '../../api/zahtjevZaNabavu'
import notifyToast from '../../components/Toast/Toast'
import {useFormik} from 'formik'
import useDebounce from '../../hooks/useDebounce'
import {getSredstvoInfo, getSredstvoIzPrijaveInfo} from '../../api/prijavaKvara'
import JmRepromaterijalaDropdown from '../../components/JmRepromaterijalaDropdown/JmRepromaterijalaDropdown'

interface NabavuDetailsModel {
    Naziv?: string
    Razlog?: string
    SredstvoINVbroj?: string
    JedMjere?: string
    Kolicina?: string
    IdPrijavaKvara?: string
    Urgentno?: string
}

const ZahtjevZaNabavuDetails = () => {
    const [loading, setLoading] = useState(false)
    const {id} = useParams()
    const idFromURL = Number(id)
    const navigate = useNavigate()

    const initialValues = {
        Naziv: undefined,
        Razlog: undefined,
        SredstvoINVbroj: undefined,
        JedMjere: undefined,
        Kolicina: undefined,
        IdPrijavaKvara: undefined,
        Urgentno: '1',
    }

    const [pagination, setpagination] = useState<PaginationModel>({
        curPage: 1,
        totalPage: 1,
        perPage: 10,
    })

    const [message, setmessage] = useState<string>()
    const [prijavaMessage, setPrijavaMessage] = useState<string>()
    const [inventurniBroj, setInventurniBroj] = useState<string>()

    const [initialData, setInitialData] = useState<NabavuDetailsModel>(initialValues)
    const [params, setparams] = useState<ParamsModelZahtjevZaNabavu>({per_page: 10, page: 1, active: 1})

    useEffect(() => {
        if (idFromURL) {
            setLoading(true)
            fetchZahtjevZaNabavu(idFromURL).then((data) => {
                setInitialData(data)
                formik.resetForm({values: data})
                setLoading(false)
            })
        } else {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idFromURL])

    const updatePage = () => {
        setLoading(true)
        getAllZahtjevZaNabavu(params)
            .then((response) => {
                const data = response.data['data']
                settableData(data)
            })
            .catch((error) => {
                console.error('Error: ', error)
                notifyToast(false, error)
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        updatePage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params])

    const validationSchema = Yup.object({
        Naziv: Yup.string().required('Obavezno polje'),
        Razlog: Yup.string().required('Obavezno polje'),
        JedMjere: Yup.string().required('Obavezno polje'),
        Kolicina: Yup.number().required('Obavezno polje'),
        Urgentno: Yup.string().required('Obavezno polje'),
    })

    const formik = useFormik({
        initialValues: initialData,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, {setStatus, setSubmitting}) => {
            setLoading(true)
            try {
                const response = await saveZahtjevZaNabavu(values)
                if (response.error === 0) {
                    notifyToast(true, response.message)
                } else {
                    notifyToast(false, response.message || 'An unexpected error occurred.')
                }
            } catch (error: any) {
                console.error('Error: ', error)
                notifyToast(false, error.response?.data?.message ?? 'Greška')
                setStatus(error)
            } finally {
                setLoading(false)
                setSubmitting(false)
            }
        },
    })

    const prijavaKvaraId = useDebounce(formik.values.IdPrijavaKvara)

    useEffect(() => {
        if (prijavaKvaraId) {
            const params = {prijavaId: prijavaKvaraId}
            getSredstvoIzPrijaveInfo(params).then((response: any) => {
                if (response?.PrijavaOpisKvara) {
                    setPrijavaMessage(response.PrijavaOpisKvara)
                    setInventurniBroj(response?.sredstvo?.SredstvoInvBroj)
                    setmessage(response?.sredstvo?.SredstvoNaziv)
                    // keep your behavior: when pulling from prijava, also fill the form field
                    formik.setFieldValue('SredstvoINVbroj', response?.sredstvo?.SredstvoInvBroj)
                } else {
                    setPrijavaMessage(undefined)
                    setmessage(undefined)
                }
            })
        } else {
            setPrijavaMessage(undefined)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prijavaKvaraId])

    const handleInputChange = (e) => {
        const {value} = e.target
        setInventurniBroj(value)
        formik.setFieldValue('SredstvoINVbroj', value)
    }

    const invBroj = useDebounce(formik.values.SredstvoINVbroj)

    useEffect(() => {
        if (invBroj) {
            const params = {invBroj: invBroj}
            getSredstvoInfo(params).then((response: any) => {
                if (response?.SredstvoNaziv) {
                    setmessage(response.SredstvoNaziv)
                } else {
                    setmessage(undefined)
                }
            })
        } else {
            setmessage(undefined)
        }
    }, [invBroj])

    const assetFound = !!message
    const canSubmit = !(loading || formik.isSubmitting || !formik.isValid || !message)

    const [tableData, settableData] = useState<ZahtjevZaNabavu[]>([])

    const columns = [
        {id: 'Id', header: 'ID', accessorKey: 'Id'},
        {id: 'Naziv', header: 'Naziv', accessorKey: 'Naziv'},
        {id: 'Prijava kvara #', header: 'Prijava kvara #', accessorKey: 'IdPrijavaKvara'},
        {id: 'Sredstvo', header: 'Sredstvo', accessorKey: 'sredstvo.SredstvoNaziv'},
        {id: 'Datum', header: 'Datum', accessorFn: (row) => `${moments(row.KreiraoDatum).format('DD-MM-YYYY HH:mm:ss')}`},
        {id: 'Kreirao', header: 'Kreirao', accessorKey: 'uposlenik.UposlenikImePrezime'},
        {
            header: 'Urgentno',
            accessorKey: 'Urgentno',
            cell: ({row}) =>
                row.original.Urgentno === 1 ? (
                    <span className='badge rounded-pill bg-danger px-3 py-2 fw-semibold'>DA</span>
                ) : (
                    <span className='badge rounded-pill bg-secondary px-3 py-2 fw-semibold'>NE</span>
                ),
        },
        {
            header: 'Status',
            accessorKey: 'Status',
            cell: ({row}) => (
                <span
                    className='badge rounded-pill px-3 py-2 fw-semibold'
                    style={{backgroundColor: row.original?.status?.Boja || '#6c757d', color: '#fff'}}
                >
          {row.original?.status?.Naziv || 'Nepoznato'}
        </span>
            ),
        },
    ]

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    })

    return (
        <div className='card mb-5 mb-xl-10'>
            <ToastContainer />

            <div
                className='card-header border-0 cursor-pointer'
                role='button'
                data-bs-target='#kt_account_profile_details'
                aria-expanded='true'
                aria-controls='kt_account_profile_details'
            >
                <div className='card-title m-0 d-flex flex-column'>
                    <TitleComponent name='Zahtjev za nabavu' />
                    <div className='text-muted fs-7 mt-1'>
                        Popunite formu — sistem će pronaći sredstvo po inventurnom broju (ili preko ID prijave kvara).
                    </div>
                </div>
            </div>

            {/* Helpful steps */}
            <div className='px-9 pt-4'>
                <div className='alert alert-info mb-4'>
                    <div className='fw-bold mb-1'>Kako unijeti zahtjev:</div>
                    <ol className='mb-0 ps-3'>
                        <li>Unesite <b>Naziv</b> proizvoda/usluge.</li>
                        <li>Ako postoji prijava kvara, unesite <b>ID prijave</b> (automatski će povući sredstvo i opis).</li>
                        <li>Provjerite <b>inventurni broj</b> i da se pojavi naziv sredstva (zelena poruka).</li>
                        <li>Odaberite <b>JM</b>, unesite <b>Količinu</b> i napišite <b>Razlog</b>.</li>
                        <li>Označite da li je <b>urgentno</b> i kliknite <b>Potvrdi</b>.</li>
                    </ol>
                </div>
            </div>

            <div className='row g-0'>
                {/* LEFT: FORM */}
                <div id='kt_account_profile_details' className='collapse show col-lg-6 border-end'>
                    {loading ? (
                        <div className='p-9'>
                            <LoadingComponent />
                        </div>
                    ) : (
                        <form onSubmit={formik.handleSubmit} noValidate className='form'>
                            <div className='card-body border-top p-9'>
                                {/* STEP 1: Naziv */}
                                <div className='mb-6'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='required fw-bold fs-6 mb-2'>1) Naziv proizvoda / usluge</label>
                                        <span className='badge bg-light text-dark border'>Korak 1</span>
                                    </div>

                                    <input
                                        type='text'
                                        placeholder='Npr. Ležaj 6204 / Servis motora / Zamjena remena...'
                                        {...formik.getFieldProps('Naziv')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            {'is-invalid': formik.touched.Naziv && formik.errors.Naziv},
                                            {'is-valid': formik.touched.Naziv && !formik.errors.Naziv}
                                        )}
                                    />
                                    {formik.touched.Naziv && formik.errors.Naziv && (
                                        <div className='fv-plugins-message-container mt-2'>
                                            <div className='fv-help-block text-danger'>{formik.errors.Naziv}</div>
                                        </div>
                                    )}
                                    <div className='form-text'>Ukratko napišite šta treba nabaviti.</div>
                                </div>

                                {/* STEP 2: Link to prijava */}
                                <div className='mb-6'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='required fw-bold fs-6 mb-2'>2) ID prijave kvara (opcionalno)</label>
                                        <span className='badge bg-light text-dark border'>Korak 2</span>
                                    </div>

                                    <input
                                        type='number'
                                        placeholder='Npr. 125'
                                        {...formik.getFieldProps('IdPrijavaKvara')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            {'is-invalid': formik.touched.IdPrijavaKvara && formik.errors.IdPrijavaKvara},
                                            {'is-valid': formik.touched.IdPrijavaKvara && !formik.errors.IdPrijavaKvara}
                                        )}
                                    />

                                    <div className='form-text'>Ako unesete ID, sistem će pokušati povući opis kvara i sredstvo.</div>

                                    {prijavaMessage ? (
                                        <div className='alert alert-primary mt-3 mb-0'>
                                            <div className='fw-bold mb-1'>Opis prijave kvara:</div>
                                            <div className='text-gray-800'>{prijavaMessage}</div>
                                        </div>
                                    ) : null}
                                </div>

                                {/* STEP 3: Inventurni broj */}
                                <div className='mb-6'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='required fw-bold fs-6 mb-2'>3) Inventurni broj sredstva</label>
                                        <span className='badge bg-light text-dark border'>Korak 3</span>
                                    </div>

                                    <input
                                        type='text'
                                        placeholder='Npr. 100234'
                                        value={inventurniBroj ?? ''}
                                        onChange={handleInputChange}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            {'is-invalid': formik.touched.SredstvoINVbroj && formik.errors.SredstvoINVbroj},
                                            {'is-valid': formik.touched.SredstvoINVbroj && !formik.errors.SredstvoINVbroj}
                                        )}
                                    />

                                    <div className='form-text'>Nakon unosa, sistem automatski provjerava sredstvo.</div>

                                    <div className='mt-3'>
                                        {formik.values.SredstvoINVbroj ? (
                                            assetFound ? (
                                                <div className='alert alert-success d-flex align-items-center mb-0 py-3'>
                                                    <div className='me-3'>
                                                        <span className='badge bg-success'>OK</span>
                                                    </div>
                                                    <div>
                                                        <div className='fw-bold'>Sredstvo pronađeno:</div>
                                                        <div className='fs-6'>{message}</div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className='alert alert-warning d-flex align-items-center mb-0 py-3'>
                                                    <div className='me-3'>
                                                        <span className='badge bg-warning text-dark'>PAŽNJA</span>
                                                    </div>
                                                    <div>
                                                        <div className='fw-bold'>Sredstvo nije pronađeno</div>
                                                        <div className='fs-7 text-muted'>Provjerite inventurni broj (da li ste pogriješili jednu cifru).</div>
                                                    </div>
                                                </div>
                                            )
                                        ) : null}
                                    </div>
                                </div>

                                {/* STEP 4: JM + Količina */}
                                <div className='mb-6'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='required fw-bold fs-6 mb-2'>4) Jedinica mjere i količina</label>
                                        <span className='badge bg-light text-dark border'>Korak 4</span>
                                    </div>

                                    <div className='row g-3'>
                                        <div className='col-md-6'>
                                            <label className='col-form-label required fw-bold fs-6 mb-2'>JM</label>
                                            <JmRepromaterijalaDropdown
                                                value={formik.values.JedMjere}
                                                onChange={(name, value) => formik.setFieldValue('JedMjere', value)}
                                                errors={formik.errors.JedMjere}
                                                touched={formik.touched.JedMjere}
                                                selectName='JedMjere'
                                            />
                                        </div>

                                        <div className='col-md-6'>
                                            <label className='col-form-label required fw-bold fs-6 mb-2'>Količina</label>
                                            <input
                                                type='number'
                                                placeholder='Npr. 2'
                                                {...formik.getFieldProps('Kolicina')}
                                                className={clsx(
                                                    'form-control form-control-lg',
                                                    {'is-invalid': formik.touched.Kolicina && formik.errors.Kolicina},
                                                    {'is-valid': formik.touched.Kolicina && !formik.errors.Kolicina}
                                                )}
                                            />
                                            {formik.touched.Kolicina && formik.errors.Kolicina && (
                                                <div className='fv-plugins-message-container mt-2'>
                                                    <div className='fv-help-block text-danger'>{formik.errors.Kolicina}</div>
                                                </div>
                                            )}
                                            <div className='form-text'>Unesite broj (bez slova).</div>
                                        </div>
                                    </div>
                                </div>

                                {/* STEP 5: Razlog */}
                                <div className='mb-6'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='required fw-bold fs-6 mb-2'>5) Razlog nabave</label>
                                        <span className='badge bg-light text-dark border'>Korak 5</span>
                                    </div>

                                    <textarea
                                        rows={4}
                                        placeholder='Npr. dio je neispravan, treba zamjena zbog kvara, nema na stanju...'
                                        {...formik.getFieldProps('Razlog')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            {'is-invalid': formik.touched.Razlog && formik.errors.Razlog},
                                            {'is-valid': formik.touched.Razlog && !formik.errors.Razlog}
                                        )}
                                    />
                                    {formik.touched.Razlog && formik.errors.Razlog && (
                                        <div className='fv-plugins-message-container mt-2'>
                                            <div className='fv-help-block text-danger'>{formik.errors.Razlog}</div>
                                        </div>
                                    )}
                                </div>

                                {/* STEP 6: Urgent */}
                                <div className='mb-2'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <label className='fw-bold fs-6 required mb-2'>6) Urgentno?</label>
                                        <span className='badge bg-light text-dark border'>Korak 6</span>
                                    </div>

                                    <div className='row g-3'>
                                        <div className='col-6'>
                                            <label
                                                htmlFor='Da'
                                                className={clsx(
                                                    'w-100 border rounded p-4 d-flex align-items-center gap-3',
                                                    // eslint-disable-next-line eqeqeq
                                                    formik.values.Urgentno == '1' ? 'border-danger bg-light' : 'border-gray-300'
                                                )}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <input
                                                    id='Da'
                                                    type='radio'
                                                    value='1'
                                                    name='urgentna'
                                                    onChange={(e) => formik.setFieldValue('Urgentno', e.target.value)}
                                                    // eslint-disable-next-line eqeqeq
                                                    checked={formik.values.Urgentno == '1'}
                                                />
                                                <div>
                                                    <div className='fw-bold'>DA</div>
                                                    <div className='text-muted fs-7'>Ne može čekati, utiče na rad.</div>
                                                </div>
                                            </label>
                                        </div>

                                        <div className='col-6'>
                                            <label
                                                htmlFor='Ne'
                                                className={clsx(
                                                    'w-100 border rounded p-4 d-flex align-items-center gap-3',
                                                    // eslint-disable-next-line eqeqeq
                                                    formik.values.Urgentno == '0' ? 'border-success bg-light' : 'border-gray-300'
                                                )}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <input
                                                    id='Ne'
                                                    type='radio'
                                                    value='0'
                                                    name='urgentna'
                                                    onChange={(e) => formik.setFieldValue('Urgentno', e.target.value)}
                                                    // eslint-disable-next-line eqeqeq
                                                    checked={formik.values.Urgentno == '0'}
                                                />
                                                <div>
                                                    <div className='fw-bold'>NE</div>
                                                    <div className='text-muted fs-7'>Može sačekati / planirati.</div>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='card-footer d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 py-6 px-9'>
                                <div className='text-muted fs-7'>
                                    {!message && formik.values.SredstvoINVbroj ? (
                                        <span>Ne možete potvrditi dok sistem ne pronađe sredstvo po inventurnom broju.</span>
                                    ) : (
                                        <span>Provjerite podatke prije potvrde.</span>
                                    )}
                                </div>

                                <button type='submit' className='btn btn-primary btn-lg' disabled={!canSubmit}>
                                    {!loading && 'Potvrdi'}
                                    {loading && (
                                        <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* RIGHT: TABLE */}
                <div className='col-lg-6'>
                    {loading ? (
                        <div className='p-9'>
                            <LoadingComponent />
                        </div>
                    ) : (
                        <div className='p-9'>
                            <div className='d-flex align-items-center justify-content-between mb-3'>
                                <div>
                                    <div className='fw-bold'>Pregled zahtjeva</div>
                                    <div className='text-muted fs-7'>Lista zadnjih zahtjeva.</div>
                                </div>
                            </div>

                            <div className='table-responsive mt-3'>
                                <table className='table table-bordered table-striped table-hover align-middle'>
                                    <thead
                                        style={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            background: '#212529',
                                            color: '#fff',
                                        }}
                                    >
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr key={headerGroup.id} className='text-start fw-bolder fs-7 text-uppercase gs-0'>
                                            {headerGroup.headers.map((header) => (
                                                <th className='text-white py-3' key={header.id}>
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                    </thead>

                                    <tbody className='text-gray-800'>
                                    {table.getRowModel().rows && table.getRowModel().rows.length > 0 ? (
                                        table.getRowModel().rows.map((row) => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <td key={cell.id} className='py-3'>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={8}>
                                                <div className='d-flex text-center w-100 align-content-center justify-content-center py-4'>
                                                    No matching records found
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            <TablePagination
                                updatePage={(page, per_page) => setparams({...params, page: page, per_page: per_page})}
                                pagination={pagination}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ZahtjevZaNabavuDetails
